import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { trackerConfigSettingsEntries } from 'src/app/classes/shared/tracker-config-display.util';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { ButtonComponent } from 'ui-library';

export interface TrackerSettingsOverviewDialogData {
  trackers: Tracker[];
}

// Inserting every selected tracker's card in one synchronous burst used to freeze the tab after
// visiting a project dashboard first - root cause was leaked Chart.js MutationObservers/resize
// listeners from dashboard widgets never being torn down, since the router detached rather than
// destroyed the dashboard route (see reuse-strategy.ts's NO_DETACH_COMPONENTS, now fixed).
// Batching the insertion is kept regardless since it's a reasonable perf smoothing for a large
// selection either way, not just a leak workaround.
const RENDER_BATCH_SIZE = 24;

// Read-only "see every selected device's settings at once" view - web counterpart of
// unifield-mobile-2's devices.page.ts bulkViewKydParams()/kyd-overview.page.ts. Never edits or
// saves anything; each tracker's settings are shown via the same flat-mapping TrackerConfigTool
// already uses for the editable form.
@Component({
  selector: 'app-tracker-settings-overview-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    MatToolbar,
    ButtonComponent,
  ],
  standalone: true,
  templateUrl: './tracker-settings-overview-dialog.component.html',
  styleUrls: ['./tracker-settings-overview-dialog.component.scss']
})
export class TrackerSettingsOverviewDialogComponent implements OnInit, OnDestroy {
  private readonly trackerConfigTool: TrackerConfigTool = inject(TrackerConfigTool);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly data: TrackerSettingsOverviewDialogData = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<TrackerSettingsOverviewDialogComponent> = inject(MatDialogRef<TrackerSettingsOverviewDialogComponent>);

  readonly trackers: Tracker[] = this.data.trackers;
  visibleTrackers: Tracker[] = [];
  private renderTimer: ReturnType<typeof setTimeout> | undefined;

  ngOnInit() {
    this.renderNextBatch();
  }

  ngOnDestroy() {
    clearTimeout(this.renderTimer);
  }

  private renderNextBatch() {
    const nextCount = Math.min(this.visibleTrackers.length + RENDER_BATCH_SIZE, this.trackers.length);
    this.visibleTrackers = this.trackers.slice(0, nextCount);

    if (nextCount < this.trackers.length) {
      this.renderTimer = setTimeout(() => this.renderNextBatch(), 0);
    }
  }

  settingsEntries(tracker: Tracker): { key: string; value: string }[] {
    const flat = this.trackerConfigTool.mapNestedToFlat(tracker.settings, tracker.type);
    return trackerConfigSettingsEntries(flat, this.translate, tracker.type);
  }

  close() {
    this.dialogRef.close();
  }
}
